import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api, SERVER_ORIGIN } from '../../services/api';
import {
  ArrowLeft, MapPin, Camera, CheckCircle, AlertCircle,
  X, Clock, Navigation, DollarSign, Wrench, Droplets, Image, Upload, WifiOff, Leaf, Receipt, MessageSquare
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useOfflinePhotoQueue, isRetryableUploadError } from '../../hooks/useOfflinePhotoQueue';
import { useReadOnly } from '../../hooks/useReadOnly';
import { useOfflineWork, queueAction } from '../../hooks/useOfflineWork';
import WinterUpsellPrompt from '../../components/crew/WinterUpsellPrompt';
import { parseDbTs, fmtClock, businessToday } from '../../utils/time';
import { shrinkPhoto } from '../../utils/image';
import CrewNoteEditor from '../../components/CrewNoteEditor';
import { NotePhotoStrip, AddNotePhotoButton } from '../../components/NotePhotos';
import SuggestTreatment from '../../components/crew/SuggestTreatment';
import WorkLine from '../../components/crew/WorkLine';
import { isFeeLine, isMowJob, workItems } from '../../utils/crewWork';
import { SiteMapCrewCard, LocksCard, WorkHereList, normalizeMarks, SiteMarksPayload } from '../../components/SiteMapEditor';

const API_BASE = SERVER_ORIGIN;

// Timestamp parsing lives in utils/time.ts. This file used to carry its own
// copy, which mishandled the bare "+00" offset Postgres emits for TIMESTAMPTZ
// columns (actual_start_time / actual_end_time) — parseDbTs normalizes it.
const parseUTC = parseDbTs;

// "Location unavailable" covered three different situations that need three
// different responses: the crew switched location off (they can fix it), the
// phone can't get a fix (nobody can fix it, just wait), or the browser has no
// geolocation at all. Keep them apart so the banner can say something useful.
type LocReason = 'denied' | 'no_fix' | 'unsupported' | null;

function useCurrentLocation() {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [status, setStatus] = useState<'unknown' | 'available' | 'unavailable'>('unknown');
  const [reason, setReason] = useState<LocReason>(null);

  const ask = useCallback(() => {
    if (!navigator.geolocation) { setStatus('unavailable'); setReason('unsupported'); return; }
    setStatus('unknown'); setReason(null);
    navigator.geolocation.getCurrentPosition(
      pos => { setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setStatus('available'); setReason(null); },
      err => {
        setStatus('unavailable');
        setReason(err?.code === 1 /* PERMISSION_DENIED */ ? 'denied' : 'no_fix');
      },
      { timeout: 8000 }
    );
  }, []);

  useEffect(() => { ask(); }, [ask]);
  return { location, status, reason, retry: ask };
}

export default function CrewJobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);   // camera (capture="environment")
  const galleryInputRef = useRef<HTMLInputElement>(null); // photo library (no capture)
  const receiptInputRef = useRef<HTMLInputElement>(null); // store receipt → job expense
  // Neighbour pair: one clock for both yards by default; a small switch keeps them separate.
  const [separateTimers, setSeparateTimers] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState(false);

  // Modal/form state
  const [skipModalOpen, setSkipModalOpen]   = useState(false);
  const [skipReason, setSkipReason]         = useState('');
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  // The winter-upsell camera, offered while the job is open (owner, Nov 4 2026: "when they open the job, not after
  // they complete") — the job and name it is about.
  const [upsellFor, setUpsellFor] = useState<{ jobId: string; name: string | null } | null>(null);
  const [completionNote, setCompletionNote] = useState('');
  const [lateEndReason, setLateEndReason]   = useState('');
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [lateStartReason, setLateStartReason] = useState('');
  // Set when the server refuses a start on location grounds. Holds the message
  // so the crew can read why, and unlocks the "Start anyway" override — nobody
  // should ever be stuck unable to start the job they are standing in front of.
  const [geofenceBlock, setGeofenceBlock] = useState<string | null>(null);
  const [bypassReason, setBypassReason]   = useState('');
  const [equipModalOpen, setEquipModalOpen] = useState(false);
  const [treatmentOpen, setTreatmentOpen] = useState(false);
  const [equipName, setEquipName]           = useState('');
  const [equipSeverity, setEquipSeverity]   = useState('minor');
  const [equipDesc, setEquipDesc]           = useState('');
  const [uploading, setUploading]           = useState(false);
  const [backdateMins, setBackdateMins]     = useState(0);      // "been here a while" on Start
  const [adjustOpen, setAdjustOpen]         = useState(false);  // fix the running timer
  const [isPartial, setIsPartial]           = useState(false);
  // Lawn-plan work riding this mow: only what the crew ticks counts as put down.
  const [planDone, setPlanDone]             = useState<number[]>([]);
  const [partialNote, setPartialNote]       = useState('');

  const { location: userLoc, status: locStatus, reason: locReason, retry: retryLocation } = useCurrentLocation();
  const readOnly = useReadOnly();

  // Phase 10 — offline photo queue
  const { isOnline, pendingCount, pending: pendingPhotos, enqueue: enqueueOffline, retry: retryPhoto, discard: discardPhoto } =
    useOfflinePhotoQueue({
      jobId: id!,
      // The queue sends every job's photos, so refresh whichever job it just sent.
      onUploaded: (sentJobId) => { qc.invalidateQueries(['job', sentJobId]); qc.invalidateQueries(['job', id]); },
    });

  const { data, isLoading, error } = useQuery(['job', id], () =>
    api.get(`/jobs/${id}`).then(r => r.data)
  );
  // The photo queue already exposes isOnline and its own pending count; these
  // are the finishes and skips waiting to be sent.
  const { pendingCount: pendingWork, refresh: refreshOffline } = useOfflineWork(() => qc.invalidateQueries(['job', id]));

  const adjustStart = useMutation(async (minutesAgo: number) => {
    await api.post(`/jobs/${id}/adjust-start`, { minutes_ago: minutesAgo });
  }, {
    onSuccess: (_d, m) => { qc.invalidateQueries(['job', id]); setAdjustOpen(false); toast.success(`Timer set to ${m} min on site`); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not adjust'); },
  });

  // "Your garage door is open" — one tap, one text to the customer, once per visit (owner, Nov 4 2026).
  const garageText = useMutation(async () => (await api.post(`/jobs/${id}/garage-door-text`)).data, {
    onSuccess: (d: any) => { toast.success(d?.message || 'Texted them'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send the text'); },
  });
  const undoComplete = useMutation(async () => {
    await api.post(`/jobs/${id}/uncomplete`);
  }, {
    onSuccess: (r: any) => { qc.invalidateQueries(['job', id]); qc.invalidateQueries('crew-schedule'); toast.success('Completion undone'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not undo'); },
  });

  const startJob = useMutation(async (override?: string) => {
    const r = await api.post(`/jobs/${id}/start`, {
      lat: userLoc?.lat, lng: userLoc?.lng,
      late_start_reason: lateStartReason.trim() || undefined,
      bypass_reason: override?.trim() || undefined,
      start_method: override ? 'geofence_prompt' : 'manual',
      backdate_minutes: backdateMins || undefined,
      pair_mode: separateTimers ? 'separate' : undefined,
    });
    return r.data;
  }, {
    onSuccess: (d: any) => {
      qc.invalidateQueries(['job', id]); qc.invalidateQueries('crew-schedule');
      toast.success(d?.also_started ? `Started — and ${d.also_started.customer_name}'s next door (one clock, split at finish)`
        : backdateMins ? `Started — clock set ${backdateMins} min back` : 'Job started!');
      setStartModalOpen(false); setLateStartReason(''); setBackdateMins(0);
      setGeofenceBlock(null); setBypassReason('');
    },
    onError: (e: any) => {
      const data = e.response?.data;
      if (data?.can_bypass) {
        // Offer the override in place rather than dead-ending on a red toast.
        setGeofenceBlock(data.message || 'Could not confirm you are at the job site.');
        setStartModalOpen(true);
        return;
      }
      // Always name the action. A bare server message can read as though a
      // different operation failed, which is how "Failed to complete job"
      // ended up being reported against the Start button.
      console.error('[START] failed', {
        jobId: id, status: e.response?.status, code: data?.code, message: data?.message, body: data,
      });
      toast.error(`Couldn't start job — ${data?.message || 'please try again'}`);
    },
  });

  const planRiders: { id: number; words: string }[] = ((data as any)?.job?.plan_riders || []).filter((r: any) => r.status === 'scheduled');
  const completeJob = useMutation(async () => {
    const body = {
      lat: userLoc?.lat, lng: userLoc?.lng,
      completion_note: completionNote.trim() || undefined,
      late_end_reason: lateEndReason.trim() || undefined,
      is_partial: isPartial || undefined,
      partial_note: isPartial ? partialNote.trim() : undefined,
      // Sent whenever there is plan work on the mow — an empty list means "none of it went down".
      plan_done: planRiders.length ? planDone : undefined,
    };
    // No signal? Keep the finish — and the minute it happened — on the phone
    // and send it when the truck comes back into coverage.
    const held = await queueAction({
      jobId: Number(id), action: 'complete', body, queuedAt: Date.now(),
      label: `Finished — ${data?.job?.customer_name || data?.job?.title || `job #${id}`}`,
    });
    if (held) return { queued: true };
    await api.post(`/jobs/${id}/complete`, { ...body, client_ts: Date.now() });
    return { queued: false };
  }, {
    onSuccess: (r: any) => {
      qc.invalidateQueries(['job', id]); qc.invalidateQueries('crew-schedule');
      toast.success(r?.queued
        ? 'Saved on your phone — it will send itself when you have signal'
        : isPartial ? 'Marked partially complete — office will see what\'s left' : 'Job completed! Great work.');
      setCompleteModalOpen(false); setCompletionNote(''); setLateEndReason(''); setIsPartial(false); setPartialNote(''); setPlanDone([]);
      refreshOffline();
    },
    onError: (e: any) => {
      // Log the real reason — status, server code and payload — so a red toast
      // is traceable instead of opaque.
      console.error('[COMPLETE] failed', {
        jobId: id,
        status: e.response?.status,
        code: e.response?.data?.code,
        message: e.response?.data?.message,
        body: e.response?.data,
      });
      toast.error(`Couldn't complete job — ${e.response?.data?.message || 'please try again'}`);
    },
  });

  const skipJob = useMutation(async () => {
    if (!skipReason.trim()) throw new Error('Reason is required');
    const body = { reason: skipReason };
    const held = await queueAction({
      jobId: Number(id), action: 'skip', body, queuedAt: Date.now(),
      label: `Skipped — ${data?.job?.customer_name || data?.job?.title || `job #${id}`}`,
    });
    if (held) return { queued: true };
    await api.post(`/jobs/${id}/skip`, { ...body, client_ts: Date.now() });
    return { queued: false };
  }, {
    onSuccess: (r: any) => {
      qc.invalidateQueries(['job', id]); qc.invalidateQueries('crew-schedule');
      // A skipped stop is off the crew's day (owner, Oct 6 2026: "MY crew sees
      // the crossed out jobs fix that"): have the day screen read itself again
      // so going back does not show the stop for a moment from the last read.
      // The morning note counts today's stops too, and is kept for five minutes.
      qc.invalidateQueries('route-session-active'); qc.invalidateQueries('crew-jobs-today'); qc.invalidateQueries('crew-all-jobs'); qc.invalidateQueries('day-brief');
      // …and so is the week's pay card: it listed the skipped stop as "TO DO"
      // with its pay for up to a minute.
      qc.invalidateQueries('crew-pay-me');
      toast.success(r?.queued ? 'Saved on your phone — it will send when you have signal' : "Stop skipped — it's off today's list. The office sees your reason.");
      setSkipModalOpen(false); setSkipReason('');
      refreshOffline();
    },
    onError: (e: any) => { toast.error(e.message || 'Failed to skip'); },
  });

  const submitEquipReport = useMutation(async () => {
    if (!equipName.trim()) throw new Error('Equipment name required');
    await api.post('/equipment-reports', {
      job_id: Number(id), equipment_name: equipName.trim(),
      severity: equipSeverity, description: equipDesc.trim() || undefined,
    });
  }, {
    onSuccess: () => {
      toast.success('Equipment issue reported — admin notified');
      setEquipModalOpen(false); setEquipName(''); setEquipSeverity('minor'); setEquipDesc('');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to submit report'); },
  });

  async function handlePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (!picked) return;
    // Shrink first: less to push on a truck's signal, less disk to fill.
    // Returns the original file untouched if anything goes wrong.
    const file = await shrinkPhoto(picked);
    // A one-off job's photo is its before photo until the job is running, its after photo from then on — the bill
    // waits on both (services/oneOffGates). The queue carries the stage so an offline photo counts the same.
    const stage: 'before' | 'after' = data?.job?.status === 'scheduled' ? 'before' : 'after';
    const stageOpts = data?.job?.photo_gate?.on ? ({ kind: 'finish', stage } as const) : undefined;

    // Try to queue offline first; if offline, the hook stores it and returns true
    const storedOffline = await enqueueOffline(file, stageOpts);
    if (storedOffline) {
      if (fileInputRef.current)   fileInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      return;
    }

    // Online path — upload immediately
    setUploading(true);
    try {
      const form = new FormData();
      form.append('photo', file);
      if (stageOpts) form.append('stage', stage);
      await api.post(`/jobs/${id}/photos`, form);
      qc.invalidateQueries(['job', id]);
      toast.success(stageOpts ? `${stage === 'before' ? 'Before' : 'After'} photo uploaded!` : 'Photo uploaded!');
    } catch (err: any) {
      // One bar of signal drops uploads halfway. Keep the photo on the phone and
      // let the queue send it, rather than losing it to an error toast.
      if (isRetryableUploadError(err) && await enqueueOffline(file, stageOpts, true)) return;
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current)    fileInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  }

  // Snap the receipt at the till: the cost lands on this job today, and the
  // bank charge is linked to it when it posts instead of being counted twice.
  async function handleReceipt(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (!picked) return;
    const file = await shrinkPhoto(picked);
    setReceiptBusy(true);
    try {
      const form = new FormData();
      form.append('receipt', file);
      const r = await api.post(`/jobs/${id}/receipt`, form);
      if (r.data?.needs_amount) {
        const typed = window.prompt('Could not read the total — what does the receipt say? (e.g. 47.80)');
        const amount = Number(String(typed || '').replace(/[^0-9.]/g, ''));
        if (amount > 0) {
          const r2 = await api.post(`/jobs/${id}/receipt/${r.data.receipt_id}/amount`, { amount });
          toast.success(r2.data?.message || 'Receipt added');
        } else {
          toast.info('Receipt photo saved — Michael can add the total later');
        }
      } else {
        toast.success(r.data?.message || 'Receipt added to this job');
      }
      qc.invalidateQueries(['job', id]);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save the receipt');
    } finally {
      setReceiptBusy(false);
      if (receiptInputRef.current) receiptInputRef.current.value = '';
    }
  }

  if (isLoading) return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  if (error || !data?.job) return (
    <div className="py-12 text-center">
      <p className="text-red-600 mb-4">Failed to load job.</p>
      <button onClick={() => navigate(-1)} className="text-brand-600 font-medium">← Back</button>
    </div>
  );

  const job = data.job;
  const photos: any[] = data.photos || [];
  const isDone = job.status === 'complete' || job.status === 'skipped';
  const isScheduled = job.status === 'scheduled';
  // The neighbour-pair partner, as far as this page is concerned. A partner
  // taken off the day (skipped) is no partner: no "Paired with…" banner, no
  // "Start timer for both". The server already leaves it out for a crew login
  // (owner, Oct 6 2026: "MY crew sees the crossed out jobs fix that"); this
  // holds the line for a page read before the skip happened.
  const pairLabel: string | null = job.pair_partner_status === 'skipped' ? null : (job.pair_partner_label || null);
  const isInProgress = job.status === 'in_progress';
  // Before and after photos on one-off work (owner, Nov 1 2026; server services/oneOffGates): Start wants a before
  // photo, Complete an after photo. A photo waiting in the offline queue counts — the finish is queued the same way.
  const photoGate: { on?: boolean; before?: boolean; after?: boolean } = job.photo_gate || {};
  const hasBefore = !!photoGate.before || photos.some((p: any) => p.kind === 'before') || pendingPhotos.some(p => p.stage === 'before');
  const hasAfter = !!photoGate.after || photos.some((p: any) => p.kind === 'after') || pendingPhotos.some(p => p.stage === 'after');
  const needsBefore = !!photoGate.on && isScheduled && !hasBefore;
  const needsAfter = !!photoGate.on && isInProgress && !hasAfter;
  // A future job is view-only — the server refuses actions on it too
  const isFuture = String(job.scheduled_date || '').slice(0, 10) > businessToday();
  const fullAddress = [job.address, job.city, job.state, job.zip_code].filter(Boolean).join(', ');
  const mapsUrl = `https://maps.apple.com/?q=${encodeURIComponent(fullAddress)}`;

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(-1)} className="mt-1 text-gray-500 active:text-gray-600"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <StatusBadge status={job.status} />
          <h1 className="text-xl font-bold text-gray-900 mt-1">{job.customer_name || job.title || 'To-do'}</h1>
          {/* "Mowing" for a mow — never the bid's title — and anything else on
              this visit as Additional (owner, Sep 30 2026: "say mowing and if
              it's anything additional add as additional not instead"). */}
          <WorkLine job={job} size="lg" className="mt-0.5" />
          {!!job.is_partial && (
            <p className="mt-1.5 text-xs font-semibold text-amber-800 bg-amber-100 rounded-lg px-2.5 py-1.5">
              Partially complete — still to do: {job.partial_note}
            </p>
          )}
          {pairLabel && (
            <p className="mt-1.5 text-sm font-bold text-white bg-indigo-500 rounded-lg px-3 py-2">
              🏘 Paired with {pairLabel} — {job.pair_partner_status === 'complete' ? 'finish this one to close out the street' : 'do both before leaving the street'}
              {job.pair_note ? <span className="block text-xs font-normal text-indigo-100 mt-0.5">{job.pair_note}</span> : null}
            </p>
          )}
        </div>
        <div className={`mt-1 flex-col items-end gap-1.5 ${readOnly ? 'hidden' : 'flex'}`}>
          {/* Report equipment issue */}
          <button onClick={() => setEquipModalOpen(true)}
            className="flex items-center gap-1 text-xs text-orange-600 border border-orange-200 rounded-lg px-2 py-1.5 active:bg-orange-50">
            <Wrench className="h-3.5 w-3.5" /> Equipment
          </button>
          {/* Spotted something the lawn needs — the offer goes to the customer */}
          <button onClick={() => setTreatmentOpen(true)}
            className="flex items-center gap-1 text-xs text-emerald-700 border border-emerald-200 rounded-lg px-2 py-1.5 active:bg-emerald-50">
            <Leaf className="h-3.5 w-3.5" /> Treatment
          </button>
        </div>
      </div>

      {/* Spray needed */}
      {(job.spray_needed || job.spray_notes) && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <Droplets className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-teal-800">Spray garden beds</p>
            {job.spray_notes && <p className="text-xs text-teal-700 mt-0.5">{job.spray_notes}</p>}
          </div>
        </div>
      )}

      {/* Location status */}
      <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${
        locStatus === 'available' ? 'bg-green-50 text-green-700 border border-green-200'
        : locStatus === 'unavailable' ? 'bg-gray-50 text-gray-500 border border-gray-200'
        : 'bg-gray-50 text-gray-500 border border-gray-200'
      }`}>
        <Navigation className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="flex-1">
          {locStatus === 'available' ? 'Location on — your arrival is recorded at start and finish'
           : locStatus === 'unknown' ? 'Checking location…'
           : locReason === 'denied' ? 'Location is off for this app. Turn it on in your phone settings so arrivals record. Jobs still start and finish without it.'
           : locReason === 'unsupported' ? "This browser can't share location. Jobs still start and finish without it."
           : "Can't get a GPS fix right now. Jobs still start and finish without it."}
        </span>
        {locStatus === 'unavailable' && locReason !== 'unsupported' && (
          <button type="button" onClick={retryLocation} className="flex-shrink-0 font-semibold text-gray-600 underline">Retry</button>
        )}
      </div>

      {/* Address + ETA */}
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        {fullAddress && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 active:opacity-70">
            <MapPin className="h-5 w-5 text-brand-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900">{fullAddress}</p>
              <p className="text-xs text-brand-600 mt-0.5">Tap to open in Maps →</p>
            </div>
          </a>
        )}
        <div className="pt-2 border-t border-gray-100 space-y-1">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Clock className="h-3.5 w-3.5" />
            {new Date(job.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </div>
          {job.time_start && (
            <div className="flex items-center gap-3 text-sm flex-wrap">
              {/* The red MUST START is an instruction. Once the job is done or
                  skipped there is nothing left to start, so it goes quiet —
                  and a job scheduled for a later date is not due "today". */}
              {job.time_is_firm && !isDone
                ? <span className="font-bold text-white bg-red-600 px-2.5 py-1 rounded-full">⏰ MUST START {fmtClock(job.time_start)}</span>
                : (
                  <span className="font-semibold text-gray-600">
                    {job.time_is_firm ? 'Due' : 'ETA'} {fmtClock(job.time_start)}
                    {!isDone && !job.time_is_firm && (
                      <span className="font-normal text-gray-500"> · anytime {isFuture ? 'that day' : 'today'}</span>
                    )}
                  </span>
                )}
              {job.estimated_minutes_snapshot && (
                <span className="text-gray-500">{job.estimated_minutes_snapshot} min estimated</span>
              )}
            </div>
          )}
          {job.cutting_height_snapshot && (
            <p className="text-xs text-gray-500">Cutting height: {job.cutting_height_snapshot}</p>
          )}
        </div>
      </div>

      {/* Crew pay */}
      {(() => {
        const pay = job.crew_pay_is_manual ? job.crew_pay_override : job.estimated_crew_pay;
        if (!pay) return null;
        return (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">
                This stop pays ${Number(pay).toFixed(2)}
                {job.crew_pay_is_manual ? ' (set by admin)' : ' (set price)'}
              </p>
              {!job.crew_pay_is_manual && job.crew_hourly_rate_snapshot && job.estimated_minutes_snapshot && (
                <p className="text-xs text-green-600">${job.crew_hourly_rate_snapshot}/hr × {job.estimated_minutes_snapshot}min{Number(job.drive_minutes_snapshot) > 0 ? ` + ${job.drive_minutes_snapshot}min drive` : ''} — the estimate pays, not the clock</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Work to do — line item descriptions (prices never sent to crew). On a
          mow, the mow's own line reads "Mowing" and the rest stay as written.
          Fee, travel and credit lines are money, not work — never listed. */}
      {(() => {
        const work = isMowJob(job)
          ? workItems(data.lineItems || [], job.customer_name, job.address)
          : (data.lineItems || []).filter((li: any) => !isFeeLine(li.description))
            .map((li: any) => ({ key: String(li.id), text: li.description, quantity: Number(li.quantity) || 1 }));
        if (!work.length) return null;
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Work to do</p>
            <ul className="space-y-1.5">
              {work.map(li => (
                <li key={li.key} className="text-sm text-gray-800 flex items-start gap-2">
                  <span className="text-brand-500 mt-0.5">•</span>
                  <span>{li.text}{li.quantity > 1 ? ` ×${li.quantity}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {(!isOnline || pendingWork > 0) && (
        <div className={`rounded-xl px-4 py-2.5 text-sm font-semibold ${isOnline ? 'bg-blue-50 text-blue-800 border border-blue-200' : 'bg-gray-800 text-white'}`}>
          {isOnline
            ? `Catching up — ${pendingWork} job${pendingWork === 1 ? '' : 's'} still sending`
            : `No signal. ${pendingWork > 0 ? `${pendingWork} finished job${pendingWork === 1 ? '' : 's'} saved on this phone — they` : 'Anything you finish'} will send when you're back in range.`}
        </div>
      )}

      {/* First mow at this yard (owner, Oct 6 2026): the crew are the first to see it — they write what the next crew needs. */}
      {job.is_first_mow && (
        <div className="rounded-xl p-4 bg-sky-50 border-2 border-sky-400">
          <p className="text-xs font-black text-sky-900 uppercase tracking-wide mb-1">🆕 First mow here</p>
          <p className="text-sm text-sky-950 font-semibold leading-snug">
            Nobody has mowed this yard for us before. While you're here, add notes for next time: gate code, dogs, where to park,
            what to skip, sprinkler heads, trouble spots. Tap the note button below, and add a photo for every visit if it helps.
          </p>
        </div>
      )}

      {/* Which plant goes in the ground, from the bid the customer signed (server: services/bushSizes — the crew get
          the sizes, never the price). Nothing to tap: it is what to load on the truck. */}
      {(job.bush_sizes || []).length > 0 && (
        <div className="rounded-xl p-4 bg-emerald-600 shadow-md">
          <p className="text-xs font-bold text-white uppercase tracking-wide mb-1">Plants for this job</p>
          <ul className="space-y-0.5">
            {(job.bush_sizes || []).map((b: any, i: number) => (
              <li key={i} className="text-base text-white font-extrabold leading-snug">
                {b.quantity > 1 ? `${b.quantity} × ` : ''}{b.description}{b.size_label ? ` — ${b.size_label}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Notes sections — the crew can fix this themselves: they are the ones
          who find out the gate code changed or the dog is loose. */}
      <div className={(job.crew_notes_snapshot || (job.crew_note_photos || []).length > 0) ? 'bg-amber-400 rounded-xl p-4 shadow-md' : 'rounded-xl p-3 border border-dashed border-gray-300'}>
        {job.crew_notes_snapshot ? (
          <>
            <p className="text-xs font-black text-amber-950 uppercase tracking-wide mb-1">⚠️ Read before you start</p>
            <p className="text-base text-amber-950 font-extrabold leading-snug">{job.crew_notes_snapshot}</p>
          </>
        ) : (job.crew_note_photos || []).length > 0 ? (
          <p className="text-xs font-black text-amber-950 uppercase tracking-wide mb-1">⚠️ Look before you start</p>
        ) : (
          <p className="text-xs text-gray-500 mb-1">No note on this job.</p>
        )}
        {(job.crew_note_photos || []).length > 0 && (
          <div className="mt-2"><NotePhotoStrip photos={job.crew_note_photos} canRemove={p => !!p.mine} onRemoved={() => qc.invalidateQueries(['job', id])} /></div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <CrewNoteEditor
            jobId={Number(id)}
            notes={job.crew_notes_snapshot}
            variant={(job.crew_notes_snapshot || (job.crew_note_photos || []).length > 0) ? 'crew' : 'admin'}
            onSaved={() => qc.invalidateQueries(['job', id])}
            photos={job.crew_note_photos}
            onPhotosChanged={() => qc.invalidateQueries(['job', id])}
            enqueueOffline={enqueueOffline}
          />
          {!readOnly && (
            <>
              <AddNotePhotoButton jobId={Number(id)} scope="this" label="Photo for this visit" onAdded={() => qc.invalidateQueries(['job', id])} enqueueOffline={enqueueOffline} />
              <AddNotePhotoButton jobId={Number(id)} scope="future" label="Photo for every visit" onAdded={() => qc.invalidateQueries(['job', id])} enqueueOffline={enqueueOffline} />
            </>
          )}
        </div>
      </div>
      {/* The site map (owner, Oct 2 2026): the locks and their codes in plain
          text first, then what gets done where, then the map itself. */}
      {job.property_id && <SiteOnThisStop propertyId={Number(job.property_id)} />}
      {/* Extras the office put on this visit — part of the stop, and their
          minutes are already in the time on this card. */}
      <JobAddons jobId={Number(id)} />
      {/* What the customer told us themselves, off their own page. */}
      {(job.customer_yard_note || job.pets_note) && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-1">From the customer</p>
          {job.customer_yard_note && <p className="text-sm text-indigo-900">{job.customer_yard_note}</p>}
          {job.pets_note && <p className="text-sm text-indigo-900 mt-0.5">Pets: {job.pets_note}</p>}
        </div>
      )}
      {job.gate_notes && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">Gate Notes</p>
          <p className="text-sm text-blue-900">{job.gate_notes}</p>
        </div>
      )}
      {job.access_notes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1">Access Notes</p>
          <p className="text-sm text-yellow-900">{job.access_notes}</p>
        </div>
      )}
      {/* Office notes (`notes`) are stripped server-side for crew — never rendered here. */}
      {job.status === 'skipped' && job.skip_reason && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Skipped — Reason</p>
          <p className="text-sm text-gray-700">{job.skip_reason}</p>
        </div>
      )}
      {job.late_start_reason && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Late Start</p>
          <p className="text-sm text-amber-900">{job.late_start_reason}</p>
        </div>
      )}
      {job.crew_completion_note && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Completion Note</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.crew_completion_note}</p>
        </div>
      )}
      {job.status === 'complete' && job.completed_at && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Job Complete</p>
            <p className="text-xs text-green-600">{parseUTC(job.completed_at)?.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</p>
          </div>
        </div>
      )}

      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center gap-3">
          <WifiOff className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-800">No signal</p>
            <p className="text-xs text-amber-700">You can still take photos — they'll upload automatically when you're back online.</p>
          </div>
        </div>
      )}

      {/* Pending uploads banner (online but has queued photos) */}
      {isOnline && pendingPhotos.some(p => p.status !== 'failed') && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <Upload className="h-4 w-4 text-blue-600 flex-shrink-0 animate-pulse" />
          <p className="text-sm text-blue-800">
            {(() => { const n = pendingPhotos.filter(p => p.status !== 'failed').length; return `${n} saved photo${n !== 1 ? 's' : ''} waiting to upload — it keeps trying on its own.`; })()}
          </p>
        </div>
      )}
      {pendingPhotos.some(p => p.status === 'failed') && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-red-800">Some photos didn't upload</p>
          {pendingPhotos.filter(p => p.status === 'failed').map(p => (
            <div key={p.key} className="flex items-center gap-2 text-xs text-red-700">
              <span className="flex-1 truncate">{p.lastError || 'Upload refused'}</span>
              <button type="button" onClick={() => retryPhoto(p.key)} className="px-2 py-1 rounded-md border border-red-300 bg-white font-semibold">Retry</button>
              <button type="button" onClick={() => { if (window.confirm('Throw this photo away?')) discardPhoto(p.key); }} className="px-2 py-1 rounded-md text-red-600 underline">Discard</button>
            </div>
          ))}
        </div>
      )}

      {/* Treatment that rides this stop, or a treatment job of its own: log what went down. */}
      <ApplicationLog jobId={Number(id)} isDone={isDone} isTreatment={/treat|spray|fertil/i.test(String(job.service_type || ''))} />

      {/* Photos */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-black uppercase tracking-tight text-gray-900">
            Photos {(photos.length + pendingCount) > 0 ? `(${photos.length + pendingCount})` : ''}
          </h2>
          {!isDone && <span className={`text-xs font-normal ${photoGate.on ? 'text-amber-800 font-semibold' : 'text-gray-500'}`}>{photoGate.on ? 'Before and after required' : 'Optional'}</span>}
        </div>
        {photoGate.on && !isDone && (
          <p className="px-4 pt-2 text-xs text-gray-600">
            A before photo to start, an after photo to finish — the bill for this job doesn't go out without both.
            {hasBefore ? ' Before ✓' : ''}{hasAfter ? ' After ✓' : ''}
          </p>
        )}
        {(photos.length > 0 || pendingPhotos.length > 0) && (
          <div className="grid grid-cols-3 gap-1.5 p-3">
            {photos.map((p: any) => (
              <a key={p.id} href={`${API_BASE}${p.url}`} target="_blank" rel="noopener noreferrer">
                <img src={`${API_BASE}${p.url}`} alt="Job" className="w-full h-24 object-cover rounded-lg border border-gray-200" />
              </a>
            ))}
            {/* Pending offline photos — show placeholder with clock icon */}
            {pendingPhotos.map((p) => {
              const objUrl = URL.createObjectURL(p.blob);
              return (
                <div key={p.key} className="relative">
                  <img src={objUrl} alt="Pending" className="w-full h-24 object-cover rounded-lg border border-amber-300 opacity-60" onLoad={() => URL.revokeObjectURL(objUrl)} />
                  <div className={`absolute inset-0 flex flex-col items-center justify-center rounded-lg ${p.status === 'failed' ? 'bg-red-900/50' : 'bg-black/30'}`}>
                    <Upload className={`h-6 w-6 text-snow opacity-80 ${p.status === 'uploading' ? 'animate-pulse' : ''}`} />
                    <span className="text-[10px] font-semibold text-snow mt-0.5">
                      {p.status === 'uploading' ? 'Uploading…' : p.status === 'failed' ? 'Failed' : 'Waiting'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!isDone && !readOnly && (
          <div className="p-3 pt-0">
            {/* Camera input — opens directly to camera */}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" />
            {/* Gallery input — shows photo library */}
            <input ref={galleryInputRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
            <div className="relative">
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="w-full py-3 rounded-xl border-2 border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-brand-400 hover:text-brand-600 active:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2">
                {uploading
                  ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600" />Uploading…</>
                  : <><Camera className="h-5 w-5" />{photoGate.on ? (isScheduled ? 'Take before photo' : 'Take after photo') : photos.length === 0 ? 'Take Photo' : 'Add Another Photo'}</>}
              </button>
              {/* Bottom-left: open photo library instead */}
              {!uploading && (
                <button
                  onClick={e => { e.stopPropagation(); galleryInputRef.current?.click(); }}
                  className="absolute bottom-1.5 left-2 flex items-center gap-1 text-xs font-medium text-brand-600 bg-white border border-gray-200 shadow-sm hover:bg-brand-50 active:bg-brand-100 transition-colors px-2 py-1 rounded-lg"
                  title="Choose from photo library">
                  <Image className="h-3.5 w-3.5" />
                  <span>Library</span>
                </button>
              )}
            </div>
            {/* Store receipt — bought something for this yard? Snap it before leaving the store. */}
            <input ref={receiptInputRef} type="file" accept="image/*" capture="environment" onChange={handleReceipt} className="hidden" />
            <button onClick={() => receiptInputRef.current?.click()} disabled={receiptBusy}
              className="mt-2 w-full py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:border-brand-400 hover:text-brand-600 active:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2">
              {receiptBusy
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600" />Reading receipt…</>
                : <><Receipt className="h-4 w-4" />Snap a store receipt for this job</>}
            </button>
          </div>
        )}
      </div>

      {/* Running timer — fix the start time */}
      {isInProgress && (
        <button onClick={() => setAdjustOpen(true)}
          className="w-full text-center text-sm text-brand-700 underline decoration-dotted underline-offset-4 py-1">
          Been here longer than the timer says? Adjust start time
        </button>
      )}
      {adjustOpen && (
        <BottomSheet title="How long have you been on site?" onClose={() => setAdjustOpen(false)}>
          <div className="grid grid-cols-4 gap-2">
            {[5, 10, 15, 20, 30, 45, 60, 90].map(m => (
              <button key={m} onClick={() => adjustStart.mutate(m)} disabled={adjustStart.isLoading}
                className="py-3 rounded-xl bg-brand-50 border border-brand-200 text-brand-800 font-bold text-sm active:bg-brand-100 disabled:opacity-50">
                {m}m
              </button>
            ))}
          </div>
        </BottomSheet>
      )}

      {/* Undo an accidental complete — same day, your own completion */}
      {job.status === 'complete' && !readOnly && (
        <button
          onClick={() => { if (window.confirm('Undo this completion? The job goes back to in-progress / scheduled.')) undoComplete.mutate(); }}
          disabled={undoComplete.isLoading}
          className="w-full py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 text-sm font-medium active:bg-gray-50 disabled:opacity-50">
          {undoComplete.isLoading ? 'Undoing…' : 'Completed by mistake? Undo'}
        </button>
      )}

      {/* Actions — a view-only account gets none of them. The server refuses
          its writes anyway, so a live-looking Start button could only ever
          produce a 403. */}
      {readOnly && !isDone && (
        <p className="text-center text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl py-3">
          View only — starting, finishing and skipping are turned off on this login.
        </p>
      )}
      {!readOnly && !isDone && isFuture && (
        <p className="text-center text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl py-3">
          Scheduled for {new Date(String(job.scheduled_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} — starts that day
        </p>
      )}
      {!readOnly && !isFuture && job.status !== 'skipped' && (
        <button type="button" disabled={garageText.isLoading}
          onClick={() => { if (window.confirm(`Text ${job.customer_name || 'the customer'} that their garage door is open?`)) garageText.mutate(); }}
          className="w-full py-3 rounded-xl border-2 border-sky-300 bg-sky-50 text-sky-800 text-sm font-semibold flex items-center justify-center gap-2 active:bg-sky-100 disabled:opacity-60">
          <MessageSquare className="h-4 w-4" /> {garageText.isLoading ? 'Texting…' : 'Garage door open? Text them'}
        </button>
      )}
      {/* Winter upsell: a photo of anything here that could use work — offered while the job is open, not after
          Complete (owner, Nov 4 2026). One tap to the camera; the X is "no thanks". */}
      {!readOnly && !isFuture && job.status !== 'skipped' && (
        <button type="button" onClick={() => setUpsellFor({ jobId: String(id), name: job.customer_name || null })}
          className="w-full py-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 text-emerald-800 text-sm font-semibold flex items-center justify-center gap-2 active:bg-emerald-100">
          <Camera className="h-4 w-4" /> See something that could use work? Snap it for Michael
        </button>
      )}
      {!readOnly && !isDone && !isFuture && (
        <div className="space-y-3 pt-2">
          {isScheduled && (
            // No before photo yet on one-off work: the button says so and opens the camera instead of starting.
            <button onClick={() => (needsBefore ? fileInputRef.current?.click() : setStartModalOpen(true))} disabled={startJob.isLoading || uploading}
              className={`press w-full py-4 rounded-xl text-lg font-bold shadow-lg disabled:opacity-50 flex items-center justify-center gap-3 ${needsBefore ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-brand-600 text-black hover:bg-brand-500 active:bg-brand-700'}`}>
              {needsBefore ? <><Camera className="h-5 w-5" /> Take a before photo to start</>
                : <><Play className="h-5 w-5" /> {pairLabel && job.pair_partner_status !== 'complete' && !separateTimers ? 'Start timer for both' : 'Start Job'}</>}
            </button>
          )}
          {isScheduled && pairLabel && job.pair_partner_status !== 'complete' && (
            <p className="text-center text-xs text-gray-500">
              {separateTimers
                ? <>This yard only. <button type="button" onClick={() => setSeparateTimers(false)} className="text-brand-600 underline">One timer for both</button></>
                : <>Starts {pairLabel}'s clock too — the street time is split between the two at finish. <button type="button" onClick={() => setSeparateTimers(true)} className="text-brand-600 underline">Separate timers</button></>}
            </p>
          )}
          {isScheduled && !(pairLabel && job.pair_partner_status !== 'complete') && (
            <p className="text-center text-xs text-gray-500">Start the job when you get there. This stop pays its set price{(() => { const p = job.crew_pay_is_manual ? job.crew_pay_override : job.estimated_crew_pay; return p ? ` — $${Number(p).toFixed(2)}` : ''; })()}, whatever the clock says; the office just needs the start-to-finish time on record.</p>
          )}
          {/* Complete is shown greyed rather than hidden: a missing button reads
              as a broken app, a disabled one teaches the order. The rule itself
              is unchanged — no Complete without Start, enforced on the server. */}
          {isScheduled && (
            <button type="button" disabled aria-disabled="true" title="Start the job first"
              className="w-full py-4 rounded-xl text-lg font-bold flex items-center justify-center gap-3 bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed">
              <CheckCircle className="h-5 w-5" /> Complete Job
            </button>
          )}
          {isInProgress && (
            <>
              {/* No Complete without Start (crew P0): the button only exists once the job is running. Photos stay optional
                  on a mow; one-off work wants its after photo first, and the button says so (services/oneOffGates). */}
              <button onClick={() => (needsAfter ? fileInputRef.current?.click() : setCompleteModalOpen(true))} disabled={completeJob.isLoading || uploading}
                className={`w-full py-4 rounded-xl text-lg font-bold shadow-lg disabled:opacity-50 flex items-center justify-center gap-3 transition-colors ${needsAfter ? 'bg-amber-400 text-black hover:bg-amber-300' : 'text-white bg-green-600 hover:bg-green-700 active:bg-green-800'}`}>
                {needsAfter ? <><Camera className="h-5 w-5" /> Take an after photo to finish</> : <><CheckCircle className="h-5 w-5" /> Complete Job</>}
              </button>
            </>
          )}
          {(isInProgress || isScheduled) && (
            <>
              <button onClick={() => setSkipModalOpen(true)}
                className="w-full py-3 rounded-xl border-2 border-gray-300 text-gray-600 text-base font-medium hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center gap-2">
                <AlertCircle className="h-4 w-4" /> Skip This Job
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Start modal (with optional late-start reason) ── */}
      {startModalOpen && (
        <BottomSheet title="Start Job" onClose={() => {
          setStartModalOpen(false); setLateStartReason('');
          setGeofenceBlock(null); setBypassReason('');
        }}>
          <div className="space-y-4">
            {geofenceBlock && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-3">
                <p className="text-sm font-semibold text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  Can't confirm you're at the property
                </p>
                <p className="text-xs text-amber-700 mt-1">{geofenceBlock}</p>
                {/* The override always stays available — a crew member must
                    never be stuck unable to start. But the copy asks them to
                    confirm they're on site rather than talking them past the
                    check, which is what "GPS is often wrong, start anyway" did. */}
                <p className="text-xs text-amber-700 mt-2">
                  Only start this job if you're actually here. Say what happened — it goes on the record.
                </p>
                <input
                  value={bypassReason}
                  onChange={e => setBypassReason(e.target.value)}
                  placeholder="e.g. parked out front, phone won't get a fix"
                  className="mt-2 w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <button
                  onClick={() => startJob.mutate(bypassReason.trim() || 'Crew confirmed on site — location check failed')}
                  disabled={startJob.isLoading}
                  className="mt-2 w-full py-3 rounded-xl bg-amber-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                  <Play className="h-4 w-4" /> I'm on site — start
                </button>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Already been here a while? <span className="text-gray-500 font-normal">(starts the clock that far back)</span>
              </label>
              <div className="grid grid-cols-5 gap-1.5">
                {[0, 10, 20, 30, 45].map(m => (
                  <button key={m} type="button" onClick={() => setBackdateMins(m)}
                    className={`press py-2 rounded-xl text-sm font-bold border ${backdateMins === m ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                    {m === 0 ? 'Now' : `${m}m`}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Late start reason <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea value={lateStartReason} onChange={e => setLateStartReason(e.target.value)} rows={2}
                placeholder="e.g. Traffic, previous job ran long, equipment issue…"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => {
                setStartModalOpen(false); setLateStartReason('');
                setGeofenceBlock(null); setBypassReason('');
              }}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={() => startJob.mutate(undefined)} disabled={startJob.isLoading}
                className="press flex-1 py-3 rounded-xl bg-brand-600 text-black font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {startJob.isLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Starting…</> : <><Play className="h-4 w-4" />Start</>}
              </button>
            </div>
          </div>
        </BottomSheet>
      )}

      {/* ── Complete modal (with note + optional late-end reason) ── */}
      {upsellFor && (
        <WinterUpsellPrompt jobId={upsellFor.jobId} customerName={upsellFor.name} onClose={() => setUpsellFor(null)}
          enqueueOffline={(file, opts, force) => enqueueOffline(file, opts, force)} />
      )}
      {completeModalOpen && (
        <BottomSheet title="Complete Job" onClose={() => { setCompleteModalOpen(false); setCompletionNote(''); setLateEndReason(''); }}>
          <div className="space-y-4">
            {job.is_first_mow && (
              <p className="rounded-xl bg-sky-50 border border-sky-300 px-3 py-2 text-sm text-sky-950">
                <span className="font-bold">First mow here.</span> Anything the next crew should know — gate, dogs, parking, what to skip?
                Add it to the yard's notes{job.crew_notes_snapshot ? '' : ' (none yet)'} before you finish, so it shows on every visit.
              </p>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Completion note <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea value={completionNote} onChange={e => setCompletionNote(e.target.value)} rows={2} autoFocus
                placeholder="Anything the admin should know about this job…"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Ran over / under schedule? <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea value={lateEndReason} onChange={e => setLateEndReason(e.target.value)} rows={2}
                placeholder="e.g. Extra trimming needed, sprinkler head in the way…"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            {planRiders.length > 0 && (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-3 space-y-2">
                <p className="text-sm font-semibold text-emerald-900">🌱 Lawn plan on this mow — tick only what you put down</p>
                {planRiders.map(r => (
                  <label key={r.id} className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-emerald-600" checked={planDone.includes(r.id)}
                      onChange={e => setPlanDone(p => e.target.checked ? [...p, r.id] : p.filter(x => x !== r.id))} />
                    <span className="text-sm text-gray-800">{r.words}</span>
                  </label>
                ))}
                <p className="text-xs text-emerald-800">Left unticked = not done today. It stays on the plan so the office can book it.</p>
              </div>
            )}
            <label className={`flex items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer ${isPartial ? 'bg-amber-50 border-amber-300' : 'border-gray-200'}`}>
              <input type="checkbox" checked={isPartial} onChange={e => setIsPartial(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-amber-600" />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">Only partially done</span>
                <span className="block text-xs text-gray-500">Rain, ran out of time, couldn't get in back — tell the office what's left</span>
              </span>
            </label>
            {isPartial && (
              <textarea value={partialNote} onChange={e => setPartialNote(e.target.value)} rows={2} autoFocus
                placeholder="What still needs to be done? (required)"
                className="w-full border border-amber-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            )}
            <div className="flex gap-3">
              <button onClick={() => { setCompleteModalOpen(false); setCompletionNote(''); setLateEndReason(''); setIsPartial(false); setPartialNote(''); }}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={() => completeJob.mutate()} disabled={completeJob.isLoading || (isPartial && !partialNote.trim())}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {completeJob.isLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Completing…</> : <><CheckCircle className="h-4 w-4" />Complete</>}
              </button>
            </div>
          </div>
        </BottomSheet>
      )}

      {/* ── Skip modal ── */}
      {skipModalOpen && (
        <BottomSheet title="Skip This Job" onClose={() => { setSkipModalOpen(false); setSkipReason(''); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason (required)</label>
              <textarea value={skipReason} onChange={e => setSkipReason(e.target.value)} rows={3} autoFocus
                placeholder="e.g. Gate locked, dog in yard, customer cancelled…"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setSkipModalOpen(false); setSkipReason(''); }}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={() => skipJob.mutate()} disabled={skipJob.isLoading || !skipReason.trim()}
                className="flex-1 py-3 rounded-xl bg-gray-700 text-white font-medium text-sm disabled:opacity-50">
                {skipJob.isLoading ? 'Skipping…' : 'Skip Job'}
              </button>
            </div>
          </div>
        </BottomSheet>
      )}

      {/* ── Equipment report modal ── */}
      {treatmentOpen && <SuggestTreatment jobId={job.id} onClose={() => setTreatmentOpen(false)} />}

      {equipModalOpen && (
        <BottomSheet title="Report Equipment Issue" onClose={() => { setEquipModalOpen(false); setEquipName(''); setEquipSeverity('minor'); setEquipDesc(''); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Equipment name</label>
              <input value={equipName} onChange={e => setEquipName(e.target.value)} autoFocus
                placeholder={'e.g. 48" Husqvarna mower, blower, trimmer…'}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Severity</label>
              <div className="flex gap-2">
                {(['minor','major','down'] as const).map(s => (
                  <button key={s} onClick={() => setEquipSeverity(s)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      equipSeverity === s
                        ? s === 'down' ? 'bg-red-600 text-white border-red-600'
                        : s === 'major' ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-yellow-400 text-yellow-900 border-yellow-400'
                        : 'bg-white text-gray-600 border-gray-300'
                    }`}>
                    {s === 'down' ? '🔴 Down' : s === 'major' ? '🟠 Major' : '🟡 Minor'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Description <span className="text-gray-500 font-normal">(optional)</span></label>
              <textarea value={equipDesc} onChange={e => setEquipDesc(e.target.value)} rows={2}
                placeholder="What happened? What's broken?"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setEquipModalOpen(false); setEquipName(''); setEquipSeverity('minor'); setEquipDesc(''); }}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={() => submitEquipReport.mutate()} disabled={submitEquipReport.isLoading || !equipName.trim()}
                className="flex-1 py-3 rounded-xl bg-orange-600 text-white font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-1.5">
                <Wrench className="h-4 w-4" />{submitEquipReport.isLoading ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    complete: 'bg-green-100 text-green-800',
    skipped: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function BottomSheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  // Escape closes it, and a tap on the dark backdrop does too — a sheet with
  // only a small ✕ is a trap on a phone held one-handed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/50"
      role="dialog" aria-modal="true" aria-label={title}
      onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Play({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

/**
 * The site map for this stop: every lock and gate with its code, what gets
 * done where, and the map (components/SiteMapEditor). The server hands it
 * only to a crew login with a visit at the property this week or next — a
 * 403 just means nothing to show, same as a yard with no map.
 */
function SiteOnThisStop({ propertyId }: { propertyId: number }) {
  const { data } = useQuery(['site-marks', propertyId],
    () => api.get(`/properties/${propertyId}/site-marks`).then(r => r.data as SiteMarksPayload), { retry: false });
  const marks = normalizeMarks(data?.marks);
  if (!marks.length) return null;
  const p = data?.property;
  const pin: [number, number] | null = p && p.lat != null && p.lng != null ? [p.lat, p.lng] : null;
  return (
    <>
      <LocksCard marks={marks} />
      <WorkHereList marks={marks} />
      <SiteMapCrewCard marks={marks} pin={pin} />
    </>
  );
}

/** What else is on this stop: bed spray, a weed treatment, a bush trim. */
function JobAddons({ jobId }: { jobId: number }) {
  const { data } = useQuery(['job-addons', jobId], () => api.get(`/mow-addons/job/${jobId}`).then(r => r.data.addons as any[]), { retry: false });
  // Fees and credits (fee_*) are money, not work — the office previewing this
  // page gets them from the server, so they are dropped here too.
  const rows = (data || []).filter(a => !String(a.addon_key || '').startsWith('fee_'));
  if (!rows.length) return null;
  return (
    <div className="bg-emerald-600 rounded-xl p-4 shadow-md">
      <p className="text-xs font-bold text-emerald-50 uppercase tracking-wide mb-1">Additional on this stop</p>
      <ul className="space-y-1">
        {rows.map(a => (
          <li key={a.id} className="text-base text-white font-extrabold leading-snug">
            {a.crew_note || a.name}
            {a.minutes ? <span className="font-medium text-emerald-50"> · about {a.minutes} min, already in your time</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The application record for a treatment visit: product, rate, conditions.
 * One form, saved before the crew drives off; the office and the state can
 * both read it later.
 */
function ApplicationLog({ jobId, isDone, isTreatment }: { jobId: number; isDone: boolean; isTreatment?: boolean }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ product: '', rate: '', area_sqft: '', wind_mph: '', temp_f: '', notes: '', round_id: null as number | null });
  const { data: logData, isLoading } = useQuery(['treatment-log', jobId], () => api.get(`/treatments/log/${jobId}`).then(r => r.data));
  const data: any[] | undefined = logData?.applications;
  // What the plan says goes down on this visit (off the Plan Cost Sheet — the
  // products actually booked, even if they slid a month).
  const planned: any[] = logData?.planned || [];
  const save = useMutation(() => api.post(`/treatments/log/${jobId}`, f), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Logged'); setF({ product: '', rate: '', area_sqft: '', wind_mph: '', temp_f: '', notes: '', round_id: null }); qc.invalidateQueries(['treatment-log', jobId]); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  const set = (k: string) => (e: React.ChangeEvent<any>) => setF(p => ({ ...p, [k]: e.target.value }));
  const logged: any[] = data || [];
  if (isLoading) return null;
  if (!planned.length && !logged.length && !isTreatment) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-black uppercase tracking-tight text-gray-900 flex items-center gap-2"><Leaf className="h-4 w-4 text-emerald-600" /> What went down</h2>
        <span className="text-xs text-gray-500">{logged.length ? `${logged.length} logged` : 'Required record'}</span>
      </div>
      {logged.length > 0 && (
        <ul className="px-4 py-2 divide-y divide-gray-100">
          {logged.map(a => (
            <li key={a.id} className="py-1.5 text-sm text-gray-700">
              <span className="font-medium">{a.product}</span>{a.rate ? ` · ${a.rate}` : ''}{a.area_sqft ? ` · ${a.area_sqft} sq ft` : ''}
              {(a.wind_mph != null || a.temp_f != null) ? <span className="text-gray-500"> · {a.wind_mph != null ? `${a.wind_mph} mph wind` : ''}{a.wind_mph != null && a.temp_f != null ? ', ' : ''}{a.temp_f != null ? `${a.temp_f}°F` : ''}</span> : null}
              {a.notes ? <span className="block text-xs text-gray-500">{a.notes}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {!isDone && planned.length > 0 && (
        <div className="px-4 pt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">The plan for this visit — tap to fill in</p>
          <ul className="space-y-1.5">
            {planned.map(pl => {
              const already = logged.some(a => String(a.product || '').toLowerCase() === String(pl.product).toLowerCase());
              return (
                <li key={pl.key}>
                  <button type="button" disabled={already}
                    onClick={() => setF(p => ({ ...p, product: pl.product, rate: pl.rate || '', area_sqft: logData?.sqft ? String(logData.sqft) : p.area_sqft, round_id: pl.round_id || null }))}
                    className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${already ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-800'}`}>
                    <span className="font-semibold">{pl.product}</span>
                    <span className="text-gray-500"> · {pl.unit === 'visit' || pl.unit === 'each' ? '1' : `${pl.amount} ${pl.unit}`}{pl.rate ? ` (${pl.rate})` : ''}</span>
                    {already && <span className="ml-1 text-xs font-semibold">· logged</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {!isDone && (
        <div className="p-4 grid grid-cols-2 gap-2">
          <input className="input col-span-2" placeholder="Product (e.g. Prodiamine 65 WDG)" value={f.product} onChange={set('product')} />
          <input className="input" placeholder="Rate (e.g. 0.5 oz / 1,000 sq ft)" value={f.rate} onChange={set('rate')} />
          <input className="input" type="number" inputMode="numeric" placeholder="Area sq ft" value={f.area_sqft} onChange={set('area_sqft')} />
          <input className="input" type="number" inputMode="numeric" placeholder="Wind mph" value={f.wind_mph} onChange={set('wind_mph')} />
          <input className="input" type="number" inputMode="numeric" placeholder="Temp °F" value={f.temp_f} onChange={set('temp_f')} />
          <textarea className="input col-span-2" rows={2} placeholder="Notes (spots skipped, what to watch)" value={f.notes} onChange={set('notes')} />
          <button onClick={() => save.mutate()} disabled={save.isLoading || !f.product.trim()}
            className="col-span-2 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">
            {save.isLoading ? 'Saving…' : 'Log this application'}
          </button>
        </div>
      )}
    </div>
  );
}
